/**
 * Filters panel presentation for the add-players drawer (SE-6949 PC3,
 * SE-6950 PC4) and — through `TableFilterDrawer` — the pipeline Table and
 * Kanban views (SE-7144, SE-7405).
 *
 * Renders IN PLACE of the drawer list — header row ("FILTERS" + CLEAR ALL),
 * the pipeline's own REASON group, the shared LISTS group (SE-8627), then
 * the Watchlist filter sections, and the Cancel / Show Results footer.
 *
 * The Watchlist sections are NOT a copy: they are the scouting
 * `FilterSheetSections` component the Watchlist drawer itself renders
 * (SE-8608). Until then this file carried its own section list, which
 * converged on the same molecules (SE-6867/SE-6871/SE-7144) but still drifted
 * — REPORT STATS and the "Current Season GPR" placeholder landed on the
 * Watchlist and never here. Only REASON, which filters the drawer's own pools
 * and has no Watchlist counterpart, lives in this file; LISTS is the shared
 * `ListsFilterSection` molecule the scenario pitch's FilterSheet renders too
 * (SE-8627).
 *
 * The drawer shell (`TableFilterDrawerView`'s `DrawerContent`) sets `!p-0`
 * and supplies NO inset of its own; every `px-6` — header, REASON/LISTS, each
 * shared section and the footer — is applied explicitly, mirroring the
 * scouting `FilterSheetView`'s per-section `px-6` pattern.
 * @remarks View component — no business logic, hooks, or state.
 */

import { TEST_ID_ADD_PLAYERS_PANEL } from "e2e/testId";
import React, { memo } from "react";

import type { FilterOption } from "@/features/scouting/components/molecules/FilterChip";
import FilterSheetSections, {
  type FilterSheetSectionsProps,
} from "@/features/scouting/components/organisms/FilterSheetSections";
import ListsFilterSection from "@/features/scouting/components/molecules/ListsFilterSection";
import RoleChipGrid from "@/features/scouting/components/molecules/RoleChipGrid";
import SectionHeader from "@/features/scouting/components/molecules/SectionHeader";

import { Pressable } from "@/components/atoms/Pressable";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import { ifWeb } from "@/utils/platform";

import type { ReasonSelection } from "../../../utils/addPlayersDrawerControls";

/** Props for {@link AddPlayersFilterPanelView}. */
export interface AddPlayersFilterPanelViewProps extends FilterSheetSectionsProps {
  /** Total active DRAFT filters, driving the CLEAR ALL affordance. */
  readonly draftActiveCount: number;
  /** Reset every draft group. */
  readonly onClearAll: () => void;
  /** Discard the draft and return to the list. */
  readonly onCancel: () => void;
  /** Commit the draft and show the filtered results (PC7). */
  readonly onShowResults: () => void;

  /* --- Reason group --- */
  readonly reasonChipOptions: readonly FilterOption[];
  readonly selectedReasons: ReasonSelection;
  readonly onToggleReason: (id: string) => void;
  readonly isReasonExpanded: boolean;
  readonly onToggleReasonSection: () => void;

  /* --- Lists group (SE-7094) --- */
  readonly isListsExpanded: boolean;
  readonly onToggleListsSection: () => void;
  readonly listOptions: readonly FilterOption[];
  readonly selectedListIds: readonly string[];
  readonly onToggleList: (id: string) => void;
  readonly onClearLists: () => void;
  readonly isListChipOpen: boolean;
  readonly onToggleListChip: () => void;
  /** Close whichever chip dropdown is open. */
  readonly onCloseChip: () => void;
}

/**
 * Render the in-drawer filters panel.
 * @param root0 - Component props; see {@link AddPlayersFilterPanelViewProps}.
 * @param root0.draftActiveCount - Total active draft filters.
 * @param root0.onClearAll - Reset every draft group.
 * @param root0.onCancel - Discard the draft and return to the list.
 * @param root0.onShowResults - Commit the draft and show results.
 * @param root0.reasonChipOptions - REASON chip options ({id,label}).
 * @param root0.selectedReasons - Selected REASON categories.
 * @param root0.onToggleReason - Toggle a REASON chip by category id.
 * @param root0.isReasonExpanded - Whether REASON is expanded.
 * @param root0.onToggleReasonSection - Toggle the REASON group.
 * @param root0.isListsExpanded - Whether LISTS is expanded (SE-7094).
 * @param root0.onToggleListsSection - Toggle the LISTS group (SE-7094).
 * @param root0.listOptions - LISTS options — the org's watchlists (SE-7094).
 * @param root0.selectedListIds - Selected list ids (SE-7094).
 * @param root0.onToggleList - Toggle a list (SE-7094).
 * @param root0.onClearLists - Clear the list selection (SE-7094).
 * @param root0.isListChipOpen - Whether the LISTS chip is open (SE-7094).
 * @param root0.onToggleListChip - Toggle the LISTS chip (SE-7094).
 * @param root0.onCloseChip - Close whichever chip dropdown is open.
 */
const AddPlayersFilterPanelView = ({
  draftActiveCount,
  onClearAll,
  onCancel,
  onShowResults,
  reasonChipOptions,
  selectedReasons,
  onToggleReason,
  isReasonExpanded,
  onToggleReasonSection,
  isListsExpanded,
  onToggleListsSection,
  listOptions,
  selectedListIds,
  onToggleList,
  onClearLists,
  isListChipOpen,
  onToggleListChip,
  onCloseChip,
  ...sections
}: AddPlayersFilterPanelViewProps) => (
  <Stack
    testID={TEST_ID_ADD_PLAYERS_PANEL.FILTER_PANEL}
    className="min-h-0 flex-1 gap-3"
  >
    {/* Header row: FILTERS + CLEAR ALL, matching the scouting FilterSheet's
        `DrawerHeader` exactly — `h-16 px-6`, not just the horizontal gutter.
        The drawer shell (`TableFilterDrawerView`) sets `!p-0` and supplies no
        inset of its own, so every gutter AND the header's own height is
        applied here, not inherited. */}
    <Stack
      direction="horizontal"
      className="h-16 items-center justify-between px-6"
    >
      <Text variant="title" className="text-content-secondary">
        FILTERS
      </Text>
      {/* SE-7450: role and label restored. Both footer actions below declare
          `accessibilityRole="button"`; this one did not, and carried no
          accessible name either — so the only thing announcing it was the
          "CLEAR ALL" text node, and it was unreachable by role or by name.
          That is also why it stopped answering `getByLabel`. */}
      <Pressable
        testID={TEST_ID_ADD_PLAYERS_PANEL.FILTER_PANEL_CLEAR_ALL}
        accessibilityRole="button"
        accessibilityLabel="Clear all filters"
        onPress={onClearAll}
        disabled={draftActiveCount === 0}
        className={draftActiveCount > 0 ? "opacity-100" : "opacity-30"}
      >
        <Text variant="caption-strong" className="text-accent-primary">
          CLEAR ALL
        </Text>
      </Pressable>
    </Stack>

    <ScrollView
      className={`flex-1 ${ifWeb("scrollbar-hide", "")}`.trim()}
      showsVerticalScrollIndicator={false}
    >
      <Stack>
        {/* ── REASON — expanded by default (PC3). Options are derived from
            the reasons the loaded candidates actually carry; see the panel
            container for why they are not a hardcoded five. Absent
            entirely when no candidate carries a displayable reason —
            an empty chip row would be a control that can do nothing. */}
        {reasonChipOptions.length > 0 ? (
          <Stack className="border-b border-outline-default">
            <SectionHeader
              title="REASON"
              isExpanded={isReasonExpanded}
              onToggle={onToggleReasonSection}
              activeCount={selectedReasons.length}
            />
            {isReasonExpanded ? (
              <Stack className="px-6 pb-4">
                <RoleChipGrid
                  options={reasonChipOptions}
                  selectedIds={selectedReasons}
                  onToggleOption={onToggleReason}
                />
              </Stack>
            ) : null}
          </Stack>
        ) : null}

        {/* ── LISTS (SE-7094) — scope the candidate pool by watchlist
            membership, the drawer counterpart of Squad Planning's Lists
            dropdown. Evaluated client-side over the drawer's own pools.
            SE-8627: the shared molecule, so the scenario pitch's "Add
            players to" panel renders the same control. */}
        <ListsFilterSection
          options={listOptions}
          selectedIds={selectedListIds}
          onToggle={onToggleList}
          onClear={onClearLists}
          isExpanded={isListsExpanded}
          onToggleSection={onToggleListsSection}
          isChipOpen={isListChipOpen}
          onToggleChip={onToggleListChip}
          onCloseChip={onCloseChip}
          loading={sections.optionsLoading}
        />

        {/* ── The Watchlist sections — the SAME component the Watchlist
            drawer renders (SE-8608), so the two cannot drift again. */}
        <FilterSheetSections
          {...sections}
          sectionTestID={TEST_ID_ADD_PLAYERS_PANEL.FILTER_SECTION}
          rangeTestID={TEST_ID_ADD_PLAYERS_PANEL.FILTER_RANGE}
        />
      </Stack>
    </ScrollView>

    {/* Footer: Cancel discards (the draft dies with this subtree), Show
        Results commits and hands the drawer the filtered-results view.
        Button styling AND spacing match the scouting FilterSheet's
        `DrawerFooter` exactly (rounded-none, bg-surface-inverse, `px-6 py-4`
        — not just a top-only `pt-1`) — the same "same component, not a
        copy" standard as the header. */}
    <Stack direction="horizontal" className="items-center gap-2 px-6 py-4">
      <Pressable
        testID={TEST_ID_ADD_PLAYERS_PANEL.FILTER_PANEL_CANCEL}
        accessibilityRole="button"
        onPress={onCancel}
        className="web:transition-colors web:duration-150 web:hover:bg-surface-subtle flex-1 items-center justify-center rounded-none border border-outline-strong py-2"
      >
        <Text variant="body-bold" className="text-content-primary">
          Cancel
        </Text>
      </Pressable>
      <Pressable
        testID={TEST_ID_ADD_PLAYERS_PANEL.FILTER_PANEL_SHOW_RESULTS}
        accessibilityRole="button"
        onPress={onShowResults}
        className="flex-1 items-center justify-center rounded-none bg-surface-inverse py-2 active:bg-surface-inverse"
      >
        <Text
          variant="title-sm"
          className="text-content-inverse dark:text-content-primary"
        >
          Show Results
        </Text>
      </Pressable>
    </Stack>
  </Stack>
);

AddPlayersFilterPanelView.displayName = "AddPlayersFilterPanelView";

export default memo(AddPlayersFilterPanelView);
